import { Injectable } from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { RoleplayWebSocketFacadeService } from './roleplay-websocket-facade.service'
import { WebSocketService } from '../websocket.service'
import {
  CloudSttTurnStateMachineService,
  CloudSttTurnState,
} from './cloud-stt-turn-state-machine.service'
import { AudioCaptureStrategyFactory } from './audio-capture-strategy-factory.service'
import { IAudioCaptureStrategy } from './audio-capture-strategy.interface'
import { AudioQueueService } from './audio-queue.service'
import {
  DialogueStateMachineService,
  DialogState,
} from './dialogue-state-machine.service'
import { PersonaData } from '../persona/persona.mapping'

/**
 * Cloud STTターンコーディネーターサービス
 *
 * @description
 * Cloud STTモードにおけるターンライフサイクル全体を調整し、
 * ユーザーターン開始から次のターン開始までの6イベントシーケンスを管理します。
 *
 * ターンライフサイクルフロー（6イベントシーケンス）:
 * 1. startTurn() → WebSocket startUserTurn呼び出し + 音声ストリーミング開始
 * 2. 暫定トランスクリプト処理 → UI状態更新（LISTENING → USER_SPEAKING）
 * 3. userTurnComplete受信 → 音声ストリーミング停止 + 状態遷移（PROCESSING）
 * 4. aiResponse受信 → 音声エンキュー + 処理完了シグナル
 * 5. aiTurnComplete受信 → ターン完了フラグ設定
 * 6. キュー完了 + aiTurnCompleteフラグ確認 → 次のターン自動開始
 *
 * @responsibilities
 * - ユーザーターン開始調整（WebSocket呼び出し、音声ストリーミング開始）
 * - 暫定トランスクリプト処理とUI状態更新
 * - ターン完了処理（音声ストリーミング停止、状態遷移）
 * - AI応答処理（音声エンキュー、処理完了シグナル）
 * - 次ターン自動開始（キュー完了 + aiTurnCompleteフラグ確認後）
 * - ターン状態遷移の調整（CloudSttTurnStateMachineServiceとの連携）
 *
 * @dependencies
 * - Inbound: RoleplaySessionPageComponent
 * - Outbound: RoleplayWebSocketFacadeService (P0), WebSocketService (P0), CloudSttTurnStateMachineService (P0), AudioCaptureStrategyFactory (P0), AudioQueueService (P0), DialogueStateMachineService (P0)
 *
 * @example
 * ```typescript
 * constructor(private turnCoordinator: CloudSttTurnCoordinatorService) {}
 *
 * async startCloudSttSession() {
 *   // ユーザーターンを開始
 *   await this.turnCoordinator.startTurn(this.sessionId, this.persona, this.deviceId);
 *   // 後は自動的にイベントシーケンスが進行し、次のターンも自動開始される
 * }
 *
 * async stopSession() {
 *   // ターンを停止
 *   await this.turnCoordinator.stopTurn();
 * }
 * ```
 *
 * @see design.md CloudSttTurnCoordinatorService section
 * @see requirements.md 要件8: Cloud STTターンライフサイクル管理
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CloudSttTurnCoordinatorService {
  /**
   * 現在のセッションID
   */
  private currentSessionId: string | null = null

  /**
   * 現在のペルソナデータ
   */
  private currentPersona: PersonaData | null = null

  /**
   * 現在のデバイスID
   */
  private currentDeviceId: string | null = null

  /**
   * 現在の音声キャプチャストラテジー
   */
  private currentStrategy: IAudioCaptureStrategy | null = null

  /**
   * 現在のターン状態を保持（初回トランスクリプト判定用）
   */
  private hasReceivedInterimTranscript = false

  constructor(
    private wsFacade: RoleplayWebSocketFacadeService,
    private wsService: WebSocketService,
    private turnStateMachine: CloudSttTurnStateMachineService,
    private strategyFactory: AudioCaptureStrategyFactory,
    private audioQueue: AudioQueueService,
    private dialogueStateMachine: DialogueStateMachineService,
  ) {
    this.setupEventListeners()
  }

  /**
   * WebSocketイベントリスナーをセットアップ
   *
   * 6イベントシーケンス全体のイベントハンドラを登録します。
   */
  private setupEventListeners(): void {
    // NOTE: Step 1, 3, 5 は WebSocketService の Observable を直接使用する。
    // RoleplayWebSocketFacadeService のメソッドは takeUntil(sessionEnded$) を含むため、
    // セッション終了後に購読が完了してしまい、シングルトンである本サービスでは
    // 2回目以降のセッションでイベントを受け取れなくなる。
    // WebSocketService の Observable は Subject ベースで takeUntil がないため永続的に機能する。

    // Step 1: ユーザーターン開始イベント
    this.wsService.userTurnStarted$.pipe(untilDestroyed(this)).subscribe(() => {
      this.handleUserTurnStarted()
    })

    // Step 2: 暫定トランスクリプトイベント（Subjectベースなので問題なし）
    this.wsFacade
      .getInterimTranscript$()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.handleInterimTranscript()
      })

    // Step 3: ユーザーターン完了イベント
    this.wsService.userTurnComplete$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.handleUserTurnComplete()
      })

    // Step 4: AI応答イベント（AudioQueueServiceが自動エンキュー済み、Subjectベースなので問題なし）
    this.wsFacade
      .getAiResponse$()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // AudioQueueServiceが自動的にエンキュー済み
        // ここでは追加処理なし
      })

    // Step 5: AIターン完了イベント
    this.wsService.aiTurnComplete$.pipe(untilDestroyed(this)).subscribe(() => {
      this.handleAiTurnComplete()
    })

    // Step 6: 次のターン開始準備完了イベント（Subjectベースなので問題なし）
    this.audioQueue
      .getReadyForNextTurn()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.prepareNextTurn()
      })
  }

  /**
   * ユーザーターンを開始
   *
   * Cloud STTターンライフサイクルを開始します。
   * 以下の処理を順次実行:
   * 1. ダイアログ状態をLISTENINGに遷移
   * 2. ターン状態をLISTENINGに遷移
   * 3. WebSocket startUserTurn呼び出し
   * 4. CloudSTTストラテジーを作成
   * 5. 音声ストリーミングは userTurnStarted イベント受信後に開始
   *
   * @param sessionId セッションID
   * @param persona ペルソナデータ
   * @param deviceId 音声デバイスID
   *
   * Requirements:
   * - 8.1: Cloud STTユーザーターン開始調整
   */
  async startTurn(
    sessionId: string,
    persona: PersonaData,
    deviceId: string,
  ): Promise<void> {
    this.currentSessionId = sessionId
    this.currentPersona = persona
    this.currentDeviceId = deviceId

    // 初回トランスクリプトフラグをリセット
    this.hasReceivedInterimTranscript = false

    // ダイアログ状態をLISTENINGに遷移
    this.dialogueStateMachine.transition(DialogState.LISTENING)

    // ターン状態をLISTENINGに遷移
    this.turnStateMachine.transition(CloudSttTurnState.LISTENING)

    // 前のストラテジーを停止（再ターン時の安全対策）
    if (this.currentStrategy != null) {
      await this.currentStrategy.stop()
      this.currentStrategy = null
    }

    // CloudSTTストラテジーを作成・初期化
    this.currentStrategy = this.strategyFactory.createStrategy('cloudSTT')
    await this.currentStrategy.initialize(deviceId)

    // WebSocket startUserTurn呼び出し
    this.wsService.startUserTurn(sessionId, persona)

    // Note: 音声ストリーミングは userTurnStarted イベント受信後に開始される
  }

  /**
   * ユーザーターン開始イベントハンドラ
   *
   * サーバーからuserTurnStartedイベントを受信したら、音声ストリーミングを開始します。
   *
   * Requirements:
   * - 8.1: 音声ストリーミング開始
   */
  private async handleUserTurnStarted(): Promise<void> {
    if (this.currentStrategy == null) {
      console.warn(
        '[CloudSttTurnCoordinator] No strategy available for userTurnStarted',
      )
      return
    }

    // 音声ストリーミングを開始
    await this.currentStrategy.start()
    console.log(
      '[CloudSttTurnCoordinator] Audio streaming started after userTurnStarted',
    )
  }

  /**
   * 暫定トランスクリプトイベントハンドラ
   *
   * 暫定トランスクリプトを受信したら、UI状態を更新します。
   * 初回のトランスクリプト受信時にダイアログ状態とターン状態を遷移します。
   *
   * @param event 暫定トランスクリプトイベント
   *
   * Requirements:
   * - 8.2: 暫定トランスクリプト処理
   */
  private handleInterimTranscript(): void {
    // 初回トランスクリプト受信時のみ状態遷移
    if (!this.hasReceivedInterimTranscript) {
      this.hasReceivedInterimTranscript = true

      // ダイアログ状態をUSER_SPEAKINGに遷移
      this.dialogueStateMachine.transition(DialogState.USER_SPEAKING)

      // ターン状態をTRANSCRIBINGに遷移
      this.turnStateMachine.transition(CloudSttTurnState.TRANSCRIBING)

      console.log('[CloudSttTurnCoordinator] User started speaking')
    }
  }

  /**
   * ユーザーターン完了イベントハンドラ
   *
   * userTurnCompleteイベントを受信したら、音声ストリーミングを停止し、
   * ダイアログ状態をPROCESSINGに遷移します。
   *
   * Requirements:
   * - 8.3: userTurnComplete処理
   */
  private async handleUserTurnComplete(): Promise<void> {
    // 音声ストリーミングを停止
    if (this.currentStrategy != null) {
      await this.currentStrategy.stop()
      console.log(
        '[CloudSttTurnCoordinator] Audio streaming stopped after userTurnComplete',
      )
    }

    // ダイアログ状態をPROCESSINGに遷移
    this.dialogueStateMachine.transition(DialogState.PROCESSING)

    // ターン状態をWAITING_AIに遷移
    this.turnStateMachine.transition(CloudSttTurnState.WAITING_AI)

    console.log(
      '[CloudSttTurnCoordinator] User turn completed, waiting for AI response',
    )
  }

  /**
   * AIターン完了イベントハンドラ
   *
   * aiTurnCompleteイベントを受信したら、ターン完了フラグを設定します。
   * キュー再生完了後に次のターンを自動開始するためのフラグです。
   *
   * Requirements:
   * - 8.5: aiTurnComplete処理
   */
  private handleAiTurnComplete(): void {
    // AudioQueueServiceにターン完了フラグを設定
    this.audioQueue.setAiTurnCompleteReceived(true)

    // ターン状態をCOMPLETEDに遷移
    this.turnStateMachine.transition(CloudSttTurnState.COMPLETED)

    console.log('[CloudSttTurnCoordinator] AI turn completed')
  }

  /**
   * 次ターン自動開始準備
   *
   * キュー再生完了かつaiTurnCompleteフラグ設定済みの場合、
   * 次のユーザーターンを自動的に開始します。
   *
   * Requirements:
   * - 8.6: キュー完了時の次ターン自動開始
   */
  private async prepareNextTurn(): Promise<void> {
    // ダイアログ状態をIDLEに遷移（次のターン開始前）
    this.dialogueStateMachine.transition(DialogState.IDLE)

    // ターン状態をIDLEに遷移（次のターン開始前）
    this.turnStateMachine.transition(CloudSttTurnState.IDLE)

    console.log('[CloudSttTurnCoordinator] Preparing next turn')

    // 次のターンを自動開始
    if (
      this.currentSessionId != null &&
      this.currentPersona != null &&
      this.currentDeviceId != null
    ) {
      await this.startTurn(
        this.currentSessionId,
        this.currentPersona,
        this.currentDeviceId,
      )
      console.log('[CloudSttTurnCoordinator] Next turn started automatically')
    }
  }

  /**
   * ターンを停止
   *
   * 現在のターンを停止し、リソースをクリーンアップします。
   *
   * Requirements:
   * - カスタム要件: ターン停止機能
   */
  async stopTurn(): Promise<void> {
    // 状態のクリアはすべて await より前に同期的に行う。
    // セッション情報を後回しにすると strategy.stop() の await 中に届いた
    // ai-turn-complete → readyForNextTurn 経由で prepareNextTurn が
    // currentSessionId を見て startTurn してしまう（SBL-149: セッション終了後の
    // 孤児STTストリーム）。currentStrategy もローカルに退避してから null にする。
    // フィールドを await 後に触ると、待機中に走った次の startTurn が設定した
    // 新しい strategy を誤って破棄しうる
    this.currentSessionId = null
    this.currentPersona = null
    this.currentDeviceId = null

    const strategy = this.currentStrategy
    this.currentStrategy = null

    // ターン状態をIDLEにリセット
    this.turnStateMachine.transition(CloudSttTurnState.IDLE)

    // 音声ストリーミングを停止
    if (strategy != null) {
      await strategy.stop()
    }

    console.log('[CloudSttTurnCoordinator] Turn stopped')
  }
}
